import { appInjector } from '../app-injector';
import * as constants from '../constants-types/constants';
import { SortDirection } from '../constants-types/Enums';
import { FilterType } from '../core-data-model/FilterInfo.model';
import { SearchState, PageState } from '../core-data-model/SearchState.model';
import { SortInfo } from '../core-data-model/SortInfo.model';
import { notificationOnError } from '../core-services/Notifications.service';
import { Table } from '../core-services/Tables/Table';
import { RegexUtilities } from '../core-utilities/Regex.utility';
import { SortUtilities, SortingFunction } from '../text-utilities/Sort.utility';

/* I really would like to change T[] to List<T>, but that would force
us to change every table which is too big for right now. Maybe a day
when I feel a little braver and less pressed for time.
*/

export abstract class ClientTable<T> extends Table<T> {
    protected rawData: T[];
    isLoading = false;

    protected sortUtilities: SortUtilities;
    protected regex: RegexUtilities;

    constructor(id: string) {
        super(id);
        this.sortUtilities = appInjector().get(SortUtilities);
        this.regex = appInjector().get(RegexUtilities);
        this.contents = [];
    }

    protected abstract fetchRawData(): Promise<T[]>;
    protected abstract filterByTerm(item: T, myRegex: RegExp): boolean;
    protected abstract getSort(sortInfo: SortInfo): SortingFunction<T>;

    clearcache() {
        this.rawData = null;
    }

    @notificationOnError()
    protected async internalSearch(searchState: SearchState) {
        let dataList = await this.internalFetchRawData();
        dataList = this.filterBySearchTerm(dataList, searchState);
        dataList = this.applySort(dataList, searchState.sortingState);
        searchState.pageState.maxResults = dataList.length;
        return this.applyPaging(dataList, searchState.pageState);
    }

    protected async internalFetchRawData() {
        if (!this.rawData) {
            this.isLoading = true;
            try {
                this.rawData = await this.fetchRawData();
            } finally {
                this.isLoading = false;
            }

            setTimeout(() => {
                this.rawData = null;
            }, constants.cacheTimeForClientTable);
        }
        return this.rawData;
    }

    private filterBySearchTerm(data: T[], searchState: SearchState) {
        const searchFilter = searchState.filterState.find(
            (filter) => filter.type === FilterType.Search
        );
        if (searchFilter) {
            const myRegEx = new RegExp(
                this.regex.escape(searchFilter.parameters.term)
            );
            return data.filter((item) => this.filterByTerm(item, myRegEx));
        }
        return data;
    }

    private applySort(data: T[], _sortState: SortInfo[]) {
        const sortingFunctions = _sortState.map((sort) =>
            this.handleSort(sort)
        );
        const compositeSort =
            this.sortUtilities.makeCompositeSort<T>(sortingFunctions);
        // The map is simply a copying techynique
        return data.map((x) => x).sort(compositeSort);
    }

    private applyPaging(data: T[], _pagingState: PageState) {
        const startingIndex =
            (_pagingState.currentPageNumber - 1) * _pagingState.resultsPerPage;
        const endingIndex =
            _pagingState.currentPageNumber * _pagingState.resultsPerPage;
        return data.filter(
            (_openInvitation, index) =>
                index >= startingIndex && index < endingIndex
        );
    }

    private handleSort(sortInfo: SortInfo): SortingFunction<T> {
        const sortToReturn: SortingFunction<T> = this.getSort(sortInfo);
        if (sortInfo.direction === SortDirection.Ascending) {
            return (x, y) => sortToReturn(x, y) * -1;
        }
        return sortToReturn;
    }
}
